import { Alert } from "react-bootstrap";
import { Link } from "react-router-dom";
import type { SlantLinkHeld } from "../../../../api/src/slant/slant-contact-link";
import { serverError } from "../../lib/api";

/**
 * The held Slant ids of a 409 of a Slant link write, or `null` for every
 * other failure. The api answers that 409 with `details.slantContactLinkHeld`
 * and `details.held` (`SlantContactLinkHeldDetails`,
 * `api/src/slant/slant-contact-link.ts`). `serverError` casts the parsed
 * body, so each field is checked, not trusted.
 * @param error The error of the failed request
 */
export function slantLinksHeld(error: unknown): SlantLinkHeld[] | null {
  const details = serverError(error)?.body?.details;
  if (details?.slantContactLinkHeld !== true) {
    return null;
  }
  const entries: unknown = details.held;
  if (!Array.isArray(entries)) {
    return [];
  }
  return entries.flatMap((entry: unknown): SlantLinkHeld[] => {
    if (typeof entry !== "object" || entry === null) return [];
    const { slantContactId, holder } = entry as {
      slantContactId?: unknown;
      holder?: unknown;
    };
    if (typeof slantContactId !== "string") return [];
    if (typeof holder !== "object" || holder === null) {
      return [{ slantContactId }];
    }
    const { id, name } = holder as { id?: unknown; name?: unknown };
    if (typeof id !== "number" || !Number.isInteger(id)) {
      return [{ slantContactId }];
    }
    return [
      {
        slantContactId,
        holder: {
          id,
          name: typeof name === "string" && name.trim() !== "" ? name : null,
        },
      },
    ];
  });
}

/**
 * The refusal of a Slant link write (R4, R5): for each held Slant id, the
 * contact that holds it, with a link to its contact page, and what to do.
 * A holder of a different firm has no name and no link (R6).
 *
 * The links are anchors: the switch to Slant renders inside a `<Form>`, and a
 * click on an anchor does not submit it.
 */
export const SlantLinkHeldAlert = ({
  held,
  slantNameOf,
}: {
  held: SlantLinkHeld[];
  /** The name of a Slant record, when the page knows it. */
  slantNameOf?: (slantContactId: string) => string | undefined;
}) => {
  const ofFirm = held.some((entry) => entry.holder !== undefined);
  return (
    <Alert variant="danger" data-testid="slant-link-held">
      <div>
        Nothing was saved. A Slant record is linked to a different contact.
      </div>
      <ul className="mb-0">
        {held.map((entry) => {
          const slantName = slantNameOf?.(entry.slantContactId);
          return (
            <li key={entry.slantContactId}>
              {slantName ?? "A Slant record"} is linked to{" "}
              {entry.holder === undefined ? (
                "a contact of a different firm"
              ) : (
                <Alert.Link
                  as={Link}
                  to={`/clients/contacts/${entry.holder.id}`}
                >
                  {entry.holder.name ?? `contact ${entry.holder.id}`}
                </Alert.Link>
              )}
              .
            </li>
          );
        })}
      </ul>
      {ofFirm && (
        <div className="mt-2">
          Remove the Slant link of that contact on the{" "}
          <Alert.Link as={Link} to="/clients/contacts/mapping">
            Contact Data Connections
          </Alert.Link>{" "}
          page, then try again.
        </div>
      )}
    </Alert>
  );
};
